import React, { useState, useEffect } from 'react';
import { Bio, Interest, BasicInfo, ContentValidationResult } from '../types';
import {
  ContentValidatorContainer,
  ContentValidatorHeader,
  ContentValidatorTitle,
  ContentValidatorDescription,
  ValidationSections,
  ValidationSection,
  SectionTitle,
  SectionContent,
  ValidationItem,
  ItemStatus,
  StatusIcon,
  StatusMessage,
  ValidationDetails,
  DetailItem,
  DetailLabel,
  DetailValue,
  ValidationSummary,
  SummaryItem,
  SummaryValue,
  SummaryLabel,
  ValidationActions,
  ActionButton,
  FixButton,
  IgnoreButton,
  ValidationProgress,
  ProgressBar,
  ProgressText,
  ContentPreview,
  PreviewTitle,
  PreviewContent,
  SuggestionList,
  SuggestionItem
} from '../../../styles/Profile/ProfilePreview.styles';

interface ContentValidatorProps {
  bio: Bio;
  interests: Interest[];
  basicInfo: BasicInfo;
  onValidationChange: (results: ContentValidationResult) => void;
}

const CONTENT_RULES = [
  {
    id: 'bio-length',
    name: 'Bio Length',
    description: 'Bio should be between 50-500 characters',
    min: 50,
    max: 500,
    weight: 25
  },
  {
    id: 'bio-content',
    name: 'Bio Content',
    description: 'Bio should be engaging and informative',
    weight: 30
  },
  {
    id: 'interests-count',
    name: 'Interests Count',
    description: 'Should have 3-20 interests selected',
    min: 3,
    max: 20,
    weight: 20
  },
  {
    id: 'basic-info',
    name: 'Basic Information',
    description: 'All required fields should be filled',
    weight: 25
  }
];

export const ContentValidator: React.FC<ContentValidatorProps> = ({
  bio,
  interests,
  basicInfo,
  onValidationChange
}) => {
  const [validationResults, setValidationResults] = useState<ContentValidationResult[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState(0);

  const validateBio = (): ContentValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];
    
    // Check bio length
    if (bio.content.length < 50) {
      errors.push('Bio is too short (minimum 50 characters)');
    } else if (bio.content.length > 500) {
      errors.push('Bio is too long (maximum 500 characters)');
    }
    
    // Check bio content quality
    const wordCount = bio.content.split(/\s+/).filter(word => word.length > 0).length;
    if (wordCount < 10) {
      warnings.push('Bio should be at least 10 words');
    }
    
    if (bio.content.includes('I am') && bio.content.split('I am').length > 3) {
      warnings.push('Avoid repetitive phrases like "I am"');
    }
    
    // Content suggestions
    if (!bio.content.includes('love') && !bio.content.includes('enjoy') && !bio.content.includes('passionate')) {
      suggestions.push('Consider mentioning what you love or enjoy');
    }
    
    if (!bio.content.includes('looking for') && !bio.content.includes('seeking')) {
      suggestions.push('Mention what you\'re looking for in a relationship');
    }
    
    return {
      field: 'bio',
      isValid: errors.length === 0,
      errors,
      warnings,
      suggestions,
      score: errors.length === 0 ? (warnings.length === 0 ? 100 : 80) : 50
    };
  };

  const validateInterests = (): ContentValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];
    
    // Check interests count
    if (interests.length < 3) {
      errors.push('Please select at least 3 interests');
    } else if (interests.length > 20) {
      errors.push('Please select no more than 20 interests');
    }
    
    // Check interest diversity
    const categories = new Set(interests.map(interest => interest.category));
    if (categories.size < 2) {
      warnings.push('Consider selecting interests from different categories');
    }
    
    // Content suggestions
    if (interests.length < 5) {
      suggestions.push('Add more interests to showcase your personality');
    }
    
    if (!interests.some(interest => interest.category === 'sports' || interest.category === 'hobbies')) {
      suggestions.push('Consider adding sports or hobby interests');
    }
    
    return {
      field: 'interests',
      isValid: errors.length === 0,
      errors,
      warnings,
      suggestions,
      score: errors.length === 0 ? (warnings.length === 0 ? 100 : 80) : 50
    };
  };

  const validateBasicInfo = (): ContentValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];
    
    // Check required fields
    if (!basicInfo.firstName.trim()) {
      errors.push('First name is required');
    }
    
    if (!basicInfo.lastName.trim()) {
      errors.push('Last name is required');
    }
    
    if (!basicInfo.location.city.trim()) {
      errors.push('City is required');
    }
    
    if (!basicInfo.location.state.trim()) {
      errors.push('State/Province is required');
    }
    
    if (!basicInfo.location.country.trim()) {
      errors.push('Country is required');
    }
    
    if (!basicInfo.occupation.trim()) {
      errors.push('Occupation is required');
    }
    
    if (!basicInfo.education.trim()) {
      errors.push('Education is required');
    }
    
    // Check age
    const age = new Date().getFullYear() - basicInfo.dateOfBirth.getFullYear();
    if (age < 18) {
      errors.push('You must be at least 18 years old');
    }
    
    if (age > 100) {
      errors.push('Please enter a valid date of birth');
    }
    
    // Content suggestions
    if (basicInfo.occupation.length < 3) {
      suggestions.push('Provide more detail about your occupation');
    }
    
    if (basicInfo.education.length < 3) {
      suggestions.push('Provide more detail about your education');
    }
    
    return {
      field: 'preferences',
      isValid: errors.length === 0,
      errors,
      warnings,
      suggestions,
      score: errors.length === 0 ? (warnings.length === 0 ? 100 : 80) : 50
    };
  };

  const validateAllContent = async () => {
    setIsValidating(true);
    setValidationProgress(0);
    
    const results: ContentValidationResult[] = [];
    
    // Validate bio
    const bioResult = validateBio();
    results.push(bioResult);
    setValidationProgress(33);
    
    // Validate interests
    const interestsResult = validateInterests();
    results.push(interestsResult);
    setValidationProgress(66);
    
    // Validate basic info
    const basicInfoResult = validateBasicInfo();
    results.push(basicInfoResult);
    setValidationProgress(100);
    
    setValidationResults(results);
    setIsValidating(false);
    
    // Calculate overall validation result
    const overallResult: ContentValidationResult = {
      isValid: results.every(result => result.isValid),
      errors: results.flatMap(result => result.errors),
      warnings: results.flatMap(result => result.warnings),
      field: 'bio' as const,
      suggestions: results.flatMap(result => result.suggestions),
      score: results.length > 0 ? Math.round(results.reduce((sum, r) => sum + (r.score || 0), 0) / results.length) : 0
    };
    
    onValidationChange(overallResult);
  };

  useEffect(() => {
    validateAllContent();
  }, [bio, interests, basicInfo]);

  const getValidationSummary = () => {
    const totalErrors = validationResults.reduce((sum, r) => sum + r.errors.length, 0);
    const totalWarnings = validationResults.reduce((sum, r) => sum + r.warnings.length, 0);
    const totalSuggestions = validationResults.reduce((sum, r) => sum + r.suggestions.length, 0);
    const validSections = validationResults.filter(r => r.isValid).length;
    const totalSections = validationResults.length;
    
    return {
      totalErrors,
      totalWarnings,
      totalSuggestions,
      validSections,
      totalSections,
      validationRate: totalSections > 0 ? (validSections / totalSections) * 100 : 0
    };
  };

  const summary = getValidationSummary();

  const renderValidationSection = (result: ContentValidationResult) => {
    const getSectionTitle = (field: string) => {
      switch (field) {
        case 'bio': return 'Bio Validation';
        case 'interests': return 'Interests Validation';
        case 'preferences': return 'Basic Information Validation';
        default: return 'Content Validation';
      }
    };

    const getSectionContent = (field: string) => {
      switch (field) {
        case 'bio': return bio.content;
        case 'interests': return interests.map(i => i.name).join(', ');
        case 'preferences': return `${basicInfo.firstName} ${basicInfo.lastName}`;
        default: return '';
      }
    };

    return (
      <ValidationSection key={result.field || 'unknown'}>
        <SectionTitle>
          {getSectionTitle(result.field || 'unknown')}
          <ItemStatus $isValid={result.isValid}>
            <StatusIcon>
              {result.isValid ? '✓' : '✗'}
            </StatusIcon>
            <StatusMessage>
              {result.isValid ? 'Valid' : 'Issues Found'}
            </StatusMessage>
          </ItemStatus>
        </SectionTitle>
        
        <SectionContent>
          <ContentPreview>
            <PreviewTitle>Content Preview:</PreviewTitle>
            <PreviewContent>{getSectionContent(result.field || 'unknown')}</PreviewContent>
          </ContentPreview>
          
          <ValidationDetails>
            {result.errors.map((error: string, index: number) => (
              <DetailItem key={index} $type="error">
                <DetailLabel $type="error">Error:</DetailLabel>
                <DetailValue>{error}</DetailValue>
              </DetailItem>
            ))}
            
            {result.warnings.map((warning: string, index: number) => (
              <DetailItem key={index} $type="warning">
                <DetailLabel $type="warning">Warning:</DetailLabel>
                <DetailValue>{warning}</DetailValue>
              </DetailItem>
            ))}
            
            {result.suggestions.length > 0 && (
              <div>
                <DetailLabel $type="warning">Suggestions:</DetailLabel>
                <SuggestionList>
                  {result.suggestions.map((suggestion: string, index: number) => (
                    <SuggestionItem key={index}>{suggestion}</SuggestionItem>
                  ))}
                </SuggestionList>
              </div>
            )}
          </ValidationDetails>
        </SectionContent>
      </ValidationSection>
    );
  };

  return (
    <ContentValidatorContainer>
      <ContentValidatorHeader>
        <ContentValidatorTitle>Content Validation</ContentValidatorTitle>
        <ContentValidatorDescription>
          We check your profile content to ensure it meets our guidelines and helps you make meaningful connections.
        </ContentValidatorDescription>
      </ContentValidatorHeader>

      {isValidating && (
        <ValidationProgress>
          <ProgressBar>
            <div 
              style={{ 
                width: `${validationProgress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '10px',
                transition: 'width 0.3s ease'
              }}
            />
          </ProgressBar>
          <ProgressText>
            Validating content... {Math.round(validationProgress)}%
          </ProgressText>
        </ValidationProgress>
      )}

      <ValidationSummary>
        <SummaryItem>
          <SummaryValue>{summary.totalSections}</SummaryValue>
          <SummaryLabel>Total Sections</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryValue>{summary.validSections}</SummaryValue>
          <SummaryLabel>Valid</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryValue>{summary.totalErrors}</SummaryValue>
          <SummaryLabel>Errors</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryValue>{summary.totalWarnings}</SummaryValue>
          <SummaryLabel>Warnings</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryValue>{summary.totalSuggestions}</SummaryValue>
          <SummaryLabel>Suggestions</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryValue>{Math.round(summary.validationRate)}%</SummaryValue>
          <SummaryLabel>Validation Rate</SummaryLabel>
        </SummaryItem>
      </ValidationSummary>

      <ValidationSections>
        {validationResults.map(result => renderValidationSection(result))}
      </ValidationSections>

      <div style={{ marginTop: '1rem' }}>
        <h4>Content Guidelines:</h4>
        {CONTENT_RULES.map(rule => (
          <div key={rule.id} style={{ marginBottom: '0.5rem' }}>
            <strong>{rule.name}</strong> ({rule.weight}%): {rule.description}
            {rule.min && rule.max && (
              <span> (Range: {rule.min}-{rule.max})</span>
            )}
          </div>
        ))}
      </div>
    </ContentValidatorContainer>
  );
};
